import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit"
import { getEntraErrorMessage } from "./utils"
import { ApiError, ApiResponse } from "@/api"
import {
   requestLogin,
   LoginResponse,
   MfaRequiredResponse,
   requestLoginVerify,
   requestLogout,
   requestVerify,
   requestActivateUser,
   requestPasswordRecovery,
   requestPasswordRecoveryConfirm,
   requestEntraLogin,
   requestEntraCallback,
} from "@/api/AuthService"
import { HttpCodes } from "@/constants/globals"
import { Token } from "@/types/index"

const INVALID_AUTH_RESPONSE_MESSAGE = "Authentication failed."

function getEntraRejectionMessage(error: unknown): string {
   const apiError = error as {
      response?: { status?: number; data?: { message?: string } }
   }
   return getEntraErrorMessage(
      apiError.response?.status,
      apiError.response?.data?.message
   )
}

interface AuthState {
   isAuthenticated: boolean
   tenantId: string
   loadingLogin: boolean
   errorLogin: number | null
   mfaRequired: boolean
   challengeId: string | null
   loadingLoginVerify: boolean
   errorLoginVerify: string | null
   loadingEntraLogin: boolean
   errorEntraLogin: string | null
   loadingEntraCallback: boolean
   errorEntraCallback: string | null
   loadingLogout: boolean
   errorLogout: number | null
   loadingVerification: boolean
   errorVerification: number | null
   loadingUserActivation: boolean
   errorUserActivation: number | null
   passwordRecoveryLoading: boolean
   passwordRecoveryError: number | null
   passwordRecoveryConfirmLoading: boolean
   passwordRecoveryConfirmError: number | null
}

const token = localStorage.getItem(Token.AUTH_TOKEN)

export const initialState: AuthState = {
   isAuthenticated: !!token, // Token exists
   tenantId: sessionStorage.getItem(Token.TENANT_ID) || "",
   loadingLogin: false,
   errorLogin: null,
   mfaRequired: false,
   challengeId: null,
   loadingLoginVerify: false,
   errorLoginVerify: null,
   loadingEntraLogin: false,
   errorEntraLogin: null,
   loadingEntraCallback: false,
   errorEntraCallback: null,
   loadingLogout: false,
   errorLogout: null,
   loadingVerification: false,
   errorVerification: null,
   loadingUserActivation: false,
   errorUserActivation: null,
   passwordRecoveryLoading: false,
   passwordRecoveryError: null,
   passwordRecoveryConfirmLoading: false,
   passwordRecoveryConfirmError: null,
}

// Thunk async login
export const login = createAsyncThunk(
   "auth/login",
   async (
      { email, password }: { email: string; password: string },
      thunkAPI
   ) => {
      try {
         const response: ApiResponse<LoginResponse | MfaRequiredResponse> =
            await requestLogin(email, password)
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(500)
      }
   }
)

export const loginVerify = createAsyncThunk(
   "auth/loginVerify",
   async (
      { challengeId, code }: { challengeId: string; code: string },
      thunkAPI
   ) => {
      try {
         const response: ApiResponse<LoginResponse> = await requestLoginVerify(
            challengeId,
            code
         )
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError & {
               response?: { data?: { message?: string } }
            }
            return thunkAPI.rejectWithValue(
               apiError.response?.data?.message ?? "An error occurred"
            )
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

export const entraLogin = createAsyncThunk(
   "auth/entraLogin",
   async (_, thunkAPI) => {
      try {
         const response = await requestEntraLogin()
         return response.data.authorizeUrl
      } catch (error) {
         return thunkAPI.rejectWithValue(getEntraRejectionMessage(error))
      }
   }
)

export const entraCallback = createAsyncThunk(
   "auth/entraCallback",
   async ({ code, state }: { code: string; state: string }, thunkAPI) => {
      try {
         const response: ApiResponse<LoginResponse> =
            await requestEntraCallback(code, state)
         return response
      } catch (error) {
         return thunkAPI.rejectWithValue(getEntraRejectionMessage(error))
      }
   }
)

export const logout = createAsyncThunk("auth/logout", async (_, thunkAPI) => {
   try {
      const response: ApiResponse<void> = await requestLogout()
      return response
   } catch (error) {
      if (error instanceof Error) {
         const apiError = error as unknown as ApiError
         return thunkAPI.rejectWithValue(apiError.status)
      }
      return thunkAPI.rejectWithValue(500)
   }
})

export const verify = createAsyncThunk(
   "auth/verify",
   async ({ token }: { token: string }, thunkAPI) => {
      try {
         const response = await requestVerify(token)
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(500)
      }
   }
)

export const activateUser = createAsyncThunk(
   "auth/activateUser",
   async (
      {
         token,
         password,
         confirmPassword,
      }: { token: string; password: string; confirmPassword: string },
      thunkAPI
   ) => {
      try {
         const response = await requestActivateUser(
            token,
            password,
            confirmPassword
         )
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(500)
      }
   }
)

export const passwordRecovery = createAsyncThunk(
   "auth/reset",
   async ({ email }: { email: string }, thunkAPI) => {
      try {
         const response = await requestPasswordRecovery(email)
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(500)
      }
   }
)

export const passwordRecoveryConfirm = createAsyncThunk(
   "auth/resetConfirm",
   async (
      {
         token,
         password,
         confirmPassword,
      }: { token: string; password: string; confirmPassword: string },
      thunkAPI
   ) => {
      try {
         const response = await requestPasswordRecoveryConfirm(
            token,
            password,
            confirmPassword
         )
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(500)
      }
   }
)

function handleAuthSuccess(state: AuthState, data: LoginResponse) {
   state.isAuthenticated = true
   localStorage.setItem(Token.AUTH_TOKEN, data.token)
}

const authSlice = createSlice({
   name: "auth",
   initialState,
   reducers: {
      resetAuthState(state) {
         state.isAuthenticated = false
         state.tenantId = ""
         localStorage.removeItem(Token.AUTH_TOKEN)
         localStorage.removeItem(Token.TEST_ALARM_TRIGGER_ENABLED)
         sessionStorage.removeItem(Token.TENANT_ID)
         sessionStorage.removeItem(Token.CUSTOMER_ID)
         sessionStorage.removeItem(Token.FACILITY_ID)
         sessionStorage.removeItem(Token.FACILITY_LEVEL_SELECTORS)
      },
   },
   extraReducers: (builder) => {
      builder
         .addCase(login.pending, (state) => {
            state.loadingLogin = true
            state.errorLogin = null
         })
         .addCase(
            login.fulfilled,
            (
               state,
               action: PayloadAction<
                  ApiResponse<LoginResponse | MfaRequiredResponse>
               >
            ) => {
               state.loadingLogin = false
               const data = action.payload.data
               if ("mfaRequired" in data) {
                  state.mfaRequired = true
                  state.challengeId = data.challengeId
                  return
               }
               handleAuthSuccess(state, data)
            }
         )
         .addCase(login.rejected, (state, action) => {
            state.loadingLogin = false
            state.errorLogin = action.payload as number
         })
         .addCase(loginVerify.pending, (state) => {
            state.loadingLoginVerify = true
            state.errorLoginVerify = null
         })
         .addCase(
            loginVerify.fulfilled,
            (state, action: PayloadAction<ApiResponse<LoginResponse>>) => {
               state.loadingLoginVerify = false
               state.mfaRequired = false
               state.challengeId = null
               handleAuthSuccess(state, action.payload.data)
            }
         )
         .addCase(loginVerify.rejected, (state, action) => {
            state.loadingLoginVerify = false
            state.errorLoginVerify = action.payload as string
         })
         .addCase(entraLogin.pending, (state) => {
            state.loadingEntraLogin = true
            state.errorEntraLogin = null
         })
         .addCase(entraLogin.fulfilled, (state) => {
            state.loadingEntraLogin = false
         })
         .addCase(entraLogin.rejected, (state, action) => {
            state.loadingEntraLogin = false
            state.errorEntraLogin = action.payload as string
         })
         .addCase(entraCallback.pending, (state) => {
            state.loadingEntraCallback = true
            state.errorEntraCallback = null
         })
         .addCase(
            entraCallback.fulfilled,
            (state, action: PayloadAction<ApiResponse<LoginResponse>>) => {
               state.loadingEntraCallback = false
               const data = action.payload?.data
               if (!data?.token) {
                  state.errorEntraCallback = INVALID_AUTH_RESPONSE_MESSAGE
                  return
               }
               handleAuthSuccess(state, data)
            }
         )
         .addCase(entraCallback.rejected, (state, action) => {
            state.loadingEntraCallback = false
            state.errorEntraCallback = action.payload as string
         })
         .addCase(logout.pending, (state) => {
            state.loadingLogout = true
            state.errorLogout = null
         })
         .addCase(logout.fulfilled, (state, _) => {
            state.loadingLogout = false
            state.isAuthenticated = false
            localStorage.removeItem(Token.AUTH_TOKEN)
            localStorage.removeItem(Token.TEST_ALARM_TRIGGER_ENABLED)
            sessionStorage.removeItem(Token.TENANT_ID)
            sessionStorage.removeItem(Token.CUSTOMER_ID)
            sessionStorage.removeItem(Token.FACILITY_ID)
            sessionStorage.removeItem(Token.FACILITY_LEVEL_SELECTORS)
         })
         .addCase(logout.rejected, (state, action) => {
            state.loadingLogout = false
            state.errorLogout = action.payload as number
         })
         .addCase(verify.pending, (state) => {
            state.loadingVerification = true
            state.errorVerification = null
         })
         .addCase(verify.fulfilled, (state, _) => {
            state.loadingVerification = false
         })
         .addCase(verify.rejected, (state, action) => {
            state.loadingVerification = false
            state.errorVerification = action.payload as number
         })
         .addCase(activateUser.pending, (state) => {
            state.loadingUserActivation = true
            state.errorUserActivation = null
         })
         .addCase(activateUser.fulfilled, (state, _) => {
            state.loadingUserActivation = false
         })
         .addCase(activateUser.rejected, (state, action) => {
            state.loadingUserActivation = false
            state.errorUserActivation = action.payload as number
         })
         .addCase(passwordRecovery.pending, (state) => {
            state.passwordRecoveryLoading = true
            state.passwordRecoveryError = null
         })
         .addCase(passwordRecovery.fulfilled, (state, _) => {
            state.passwordRecoveryLoading = false
         })
         .addCase(passwordRecovery.rejected, (state, action) => {
            state.passwordRecoveryLoading = false
            state.passwordRecoveryError = action.payload as number
         })
         .addCase(passwordRecoveryConfirm.pending, (state) => {
            state.passwordRecoveryConfirmLoading = true
            state.passwordRecoveryConfirmError = null
         })
         .addCase(passwordRecoveryConfirm.fulfilled, (state, _) => {
            state.passwordRecoveryConfirmLoading = false
         })
         .addCase(passwordRecoveryConfirm.rejected, (state, action) => {
            state.passwordRecoveryConfirmLoading = false
            state.passwordRecoveryConfirmError = action.payload as number
         })
   },
})

export const LOGOUT_FULFILLED = logout.fulfilled.type
export const RESET_AUTH_STATE = authSlice.actions.resetAuthState.type
export const { resetAuthState } = authSlice.actions
export default authSlice.reducer
